import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { fmtL, fmtDate, apiErr } from "@/lib/format";
import { fileToCompressedDataUrl } from "@/lib/image";
import { toast } from "sonner";
import { useAuth, useFechaOperacion } from "@/contexts/AuthContext";
import { ApproveModal, DistributionModal, MotivoModal } from "@/pages/_expenses/ExpenseModals";
import ExpenseReportExtras, { ExpensesConfigPanel } from "@/pages/_expenses/ExpenseReportExtras";
import {
  PlusIcon, XIcon, CheckCircleIcon, XCircleIcon, MoneyIcon, ClockIcon,
  PencilSimpleIcon, TrashIcon, ArrowUUpLeftIcon, PaperPlaneTiltIcon, TagIcon, GearIcon,
  CameraIcon, FileTextIcon, EyeIcon, FloppyDiskIcon, PiggyBankIcon, ChartBarIcon, PercentIcon,
  ChatTextIcon, LightbulbIcon, PaperclipIcon
} from "@phosphor-icons/react";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";

const ESTADOS = {
  borrador: { bg: "bg-text-muted/20", text: "text-text-muted", l: "Borrador" },
  enviado: { bg: "bg-agro-yellow/20", text: "text-agro-yellow", l: "Enviado" },
  aprobado: { bg: "bg-blue-500/20", text: "text-blue-400", l: "Aprobado" },
  denegado: { bg: "bg-red-500/20", text: "text-red-400", l: "Denegado" },
  devuelto: { bg: "bg-orange-500/20", text: "text-orange-400", l: "Devuelto" },
  reembolsado: { bg: "bg-agro-green/20", text: "text-agro-green", l: "Reembolsado" },
};

const METODOS = [
  { v: "efectivo_propio", l: "Efectivo propio (de mi bolsillo)" },
  { v: "contra_adelanto", l: "Contra adelanto recibido" },
  { v: "tarjeta_empresa", l: "Tarjeta de la empresa" },
];

export default function Expenses() {
  const { user, hasPermission } = useAuth();
  const fechaOp = useFechaOperacion();
  const [profile, setProfile] = useState(null);
  const canApprove = user?.role === "admin" || hasPermission?.("expenses.approve") || !!profile?.can_approve;
  const isAdmin = user?.role === "admin";
  const canReimburse = user?.role === "admin" || user?.role === "accountant";

  const [tab, setTab] = useState("gastos");
  const [items, setItems] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [reps, setReps] = useState([]);
  const [clients, setClients] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [advances, setAdvances] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtros
  const [filtros, setFiltros] = useState({ estado: "", categoria_id: "", rtc_id: "", region: "", desde: "", hasta: "" });

  // Modales
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    categoria_id: "", descripcion: "", monto: 0, region_ruta: "",
    cliente_id: "", rtc_id: "", metodo_pago: "efectivo_propio", adelanto_id: "", notas: ""
  });
  const [reembModal, setReembModal] = useState(null);
  const [approveModal, setApproveModal] = useState(null);
  const [motivoModal, setMotivoModal] = useState(null);   // {kind: "reject"|"return", id}
  const [reembCuenta, setReembCuenta] = useState("");
  const [detail, setDetail] = useState(null);
  const [distModal, setDistModal] = useState(null);    // para asignar %s
  const [advModal, setAdvModal] = useState(false);     // crear anticipo
  const [advForm, setAdvForm] = useState({ beneficiario_id: "", beneficiario_tipo: "rtc", monto: 0, motivo: "", cuenta_id: "" });

  const load = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filtros).filter(([_, v]) => v));
      const [e, c, r, cli, a, adv] = await Promise.all([
        api.get("/expenses", { params }),
        api.get("/expenses/categories"),
        api.get("/representatives").catch(() => ({ data: [] })),
        api.get("/clients").catch(() => ({ data: [] })),
        api.get("/treasury/accounts").catch(() => ({ data: [] })),
        api.get("/advances").catch(() => ({ data: [] })),
      ]);
      setItems(e.data); setCategorias(c.data); setReps(r.data); setClients(cli.data);
      setAccounts(a.data); setAdvances(adv.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { api.get("/expenses/my-profile").then(({ data }) => setProfile(data)).catch(() => {}); }, []);
  const emptyForm = () => ({
    categoria_id: "", categoria_otro: "", descripcion: "", monto: 0, fecha: new Date().toISOString().slice(0, 10),
    region_ruta: profile?.region || "", cliente_id: "", rtc_id: profile?.rtc?.id || "",
    metodo_pago: "efectivo_propio", adelanto_id: "", notas: "",
  });
  const catOtros = (cid) => { const c = categorias.find((x) => x.id === cid); return !!c && (["OTRO", "OTROS"].includes((c.codigo || "").toUpperCase()) || ["otro", "otros"].includes((c.nombre || "").trim().toLowerCase())); };
  const needsReimburse = (it) => it.estado === "aprobado" && (it.estado_reembolso === "reembolso_pendiente" || !it.asiento_aprobacion_id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filtros]);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (!form.categoria_id) { toast.error("Selecciona una categoría"); return; }
      if (catOtros(form.categoria_id) && !(form.categoria_otro || "").trim()) { toast.error("Especifica el tipo de gasto para la categoría 'Otros'"); return; }
      const payload = { ...form, monto: Number(form.monto), ...(fechaOp.fija ? { fecha: fechaOp.hoy } : {}) };
      if (editing) await api.put(`/expenses/${editing.id}`, payload);
      else await api.post("/expenses", payload);
      toast.success(editing ? "Borrador actualizado" : "Borrador creado. Agrega respaldo antes de enviar.");
      setOpen(false); setEditing(null);
      setForm(emptyForm());
      load();
    } catch (er) { toast.error(apiErr(er)); }
  };

  const submit = async (id) => {
    try { const { data } = await api.post(`/expenses/${id}/submit`); toast.success("Enviado a revisión"); load(); setDetail(data); }
    catch (er) { toast.error(apiErr(er)); }
  };
  const approve = (id) => { const e = items.find((x) => x.id === id) || detail; if (e) setApproveModal(e); };
  const reject = (id) => setMotivoModal({ kind: "reject", id });
  const ret = (id) => setMotivoModal({ kind: "return", id });
  const confirmMotivo = async (motivo) => {
    const { kind, id } = motivoModal;
    try {
      await api.post(`/expenses/${id}/${kind}`, { motivo });
      toast.success(kind === "reject" ? "Denegada" : "Devuelta para corrección");
      setMotivoModal(null); setDetail(null); load();
    } catch (er) { toast.error(apiErr(er)); }
  };
  const del = async (id) => { if (!window.confirm("¿Eliminar?")) return; try { await api.delete(`/expenses/${id}`); toast.success("Eliminada"); load(); } catch (er) { toast.error(apiErr(er)); } };
  const crearAnticipo = async () => {
    try {
      await api.post("/advances", { ...advForm, monto: Number(advForm.monto) });
      toast.success("Anticipo entregado");
      setAdvModal(false); load();
    } catch (er) { toast.error(apiErr(er)); }
  };
  const reimburse = async (e) => { e.preventDefault(); try { await api.post(`/expenses/${reembModal.id}/reimburse`, { cuenta_id: reembCuenta || null }); toast.success("Reembolso registrado"); setReembModal(null); setReembCuenta(""); load(); } catch (er) { toast.error(apiErr(er)); } };

  const uploadAttachment = async (eid, file, descripcion) => {
    if (!file) return;
    try {
      const url = await fileToCompressedDataUrl(file);
      const { data } = await api.post(`/expenses/${eid}/attachments`, { url, descripcion: descripcion || file.name });
      toast.success("Respaldo agregado");
      setDetail(data); load();
    } catch (er) { toast.error(apiErr(er) || er.message); }
  };
  const removeAttachment = async (eid, idx) => {
    try { const { data } = await api.delete(`/expenses/${eid}/attachments/${idx}`); toast.success("Respaldo eliminado"); setDetail(data); load(); }
    catch (er) { toast.error(apiErr(er)); }
  };

  const kpis = {
    borrador: items.filter((i) => i.estado === "borrador").length,
    enviadas: items.filter((i) => i.estado === "enviado").length,
    devueltas: items.filter((i) => i.estado === "devuelto").length,
    aprobadas: items.filter((i) => i.estado === "aprobado").length,
    monto_pendiente: items.filter((i) => i.estado === "enviado").reduce((s, i) => s + i.monto, 0),
    monto_por_reembolsar: items.filter(needsReimburse).reduce((s, i) => s + i.monto, 0),
  };

  const em = useModalForm({ value: form, abierto: open, onClose: () => setOpen(false), onSave: save });
  const am = useModalForm({ value: advForm, abierto: Boolean(advModal), onClose: () => setAdvModal(false), onSave: crearAnticipo });
  const rm = useModalForm({ value: reembCuenta, abierto: Boolean(reembModal), onClose: () => setReembModal(null), onSave: reimburse });
  return (
    <Layout title="Viáticos y Gastos" subtitle={`${items.length} solicitudes · Borrador → Enviado → Aprobado / Denegado / Devuelto → Reembolsado`}
      actions={
        tab === "gastos" ? (
          <button onClick={() => { setEditing(null); setForm(emptyForm()); setOpen(true); }} data-testid="new-expense-btn"
            className="bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md px-4 py-2 text-sm flex items-center gap-2"><PlusIcon size={16} weight="bold" /> Nuevo gasto</button>
        ) : tab === "anticipos" && canReimburse ? (
          <button onClick={() => { setAdvForm({ beneficiario_id: "", beneficiario_tipo: "rtc", monto: 0, motivo: "", cuenta_id: "" }); setAdvModal(true); }} data-testid="new-advance-btn"
            className="bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md px-4 py-2 text-sm flex items-center gap-2"><PlusIcon size={16} weight="bold" /> Nuevo anticipo</button>
        ) : null
      }
    >
      <div className="flex gap-1 mb-4 border-b border-border/60">
        {[
          {k: "gastos", l: "Bandeja de gastos", icon: MoneyIcon},
          {k: "anticipos", l: "Anticipos", icon: PiggyBankIcon},
          {k: "reporte", l: "Reporte", icon: ChartBarIcon},
          {k: "categorias", l: "Categorías (configurables)", icon: TagIcon},
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`tab-${t.k}`} className={`px-4 py-2 text-sm font-medium flex items-center gap-2 border-b-2 ${tab === t.k ? "border-agro-green text-agro-green" : "border-transparent text-text-secondary hover:text-text-primary"}`}>
            <t.icon size={16} /> {t.l}
          </button>
        ))}
      </div>

      {tab === "gastos" && <>
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
          <Kpi label="Borrador" value={kpis.borrador} color="text-text-muted" />
          <Kpi label="Enviadas" value={kpis.enviadas} color="text-agro-yellow" />
          <Kpi label="Devueltas" value={kpis.devueltas} color="text-orange-400" />
          <Kpi label="Aprobadas" value={kpis.aprobadas} color="text-blue-400" />
          <Kpi label="Monto enviado" value={fmtL(kpis.monto_pendiente)} color="text-text-primary" small />
          <Kpi label="Por reembolsar" value={fmtL(kpis.monto_por_reembolsar)} color="text-agro-green" small />
        </div>

        {/* Filtros */}
        <div className="bg-card border border-border/60 rounded-lg p-3 mb-4">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-xs">
            <select value={filtros.estado} onChange={(e) => setFiltros({...filtros, estado: e.target.value})} className="bg-app border border-border rounded px-2 py-1.5" data-testid="filter-estado">
              <option value="">Todos los estados</option>
              {Object.entries(ESTADOS).map(([k, v]) => <option key={k} value={k}>{v.l}</option>)}
            </select>
            <select value={filtros.categoria_id} onChange={(e) => setFiltros({...filtros, categoria_id: e.target.value})} className="bg-app border border-border rounded px-2 py-1.5" data-testid="filter-categoria">
              <option value="">Todas las categorías</option>
              {categorias.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
            <select value={filtros.rtc_id} onChange={(e) => setFiltros({...filtros, rtc_id: e.target.value})} className="bg-app border border-border rounded px-2 py-1.5" data-testid="filter-rtc">
              <option value="">Todos los RTC</option>
              {reps.map((r) => <option key={r.id} value={r.id}>{r.codigo} · {r.nombre}</option>)}
            </select>
            <input placeholder="Región / ruta…" value={filtros.region} onChange={(e) => setFiltros({...filtros, region: e.target.value})} className="bg-app border border-border rounded px-2 py-1.5" data-testid="filter-region" />
            <input type="date" value={filtros.desde} onChange={(e) => setFiltros({...filtros, desde: e.target.value})} className="bg-app border border-border rounded px-2 py-1.5" data-testid="filter-desde" />
            <input type="date" value={filtros.hasta} onChange={(e) => setFiltros({...filtros, hasta: e.target.value})} className="bg-app border border-border rounded px-2 py-1.5" data-testid="filter-hasta" />
          </div>
        </div>

        {/* Tabla */}
        <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-sidebar">
              <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                <th className="p-3 pl-5">N° / Fecha</th><th className="p-3">Solicitante</th>
                <th className="p-3">Categoría</th><th className="p-3">Descripción</th>
                <th className="p-3">Región</th><th className="p-3">Método</th>
                <th className="p-3 text-center">Respaldo</th>
                <th className="p-3 text-right">Monto</th><th className="p-3">Estado</th>
                <th className="p-3 text-right pr-5">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan="10" className="p-8 text-center text-text-muted">Cargando…</td></tr>
                : items.length === 0 ? <tr><td colSpan="10" className="p-8 text-center text-text-muted">Sin solicitudes.</td></tr>
                : items.map((it) => {
                  const est = ESTADOS[it.estado];
                  const isOwner = it.solicitante_email === user?.email;
                  return (
                    <tr key={it.id} className="border-t border-border/40 hover:bg-white/[0.02] cursor-pointer" data-testid={`exp-row-${it.numero}`} onClick={() => setDetail(it)}>
                      <td className="p-3 pl-5"><div className="font-mono text-xs text-agro-green">{it.numero}</div><div className="text-[10px] text-text-muted">{fmtDate(it.fecha)}</div></td>
                      <td className="p-3 text-xs">{it.solicitante_nombre || it.solicitante_email}</td>
                      <td className="p-3 text-xs"><span className="px-2 py-0.5 bg-app rounded">{it.categoria_codigo || "—"} {it.categoria_nombre}</span></td>
                      <td className="p-3 text-xs max-w-[180px] truncate">{it.descripcion}</td>
                      <td className="p-3 text-xs">{it.region_ruta || "—"}</td>
                      <td className="p-3 text-[10px] uppercase text-text-muted">{(METODOS.find((m) => m.v === it.metodo_pago) || {}).l?.split(" ")[0]}</td>
                      <td className="p-3 text-center"><span className={`text-xs ${(it.comprobantes || []).length > 0 ? "text-agro-green" : "text-text-muted"}`}><PaperclipIcon size={12} className="inline align-[-2px]" aria-hidden="true" /> {(it.comprobantes || []).length}<span className="sr-only"> comprobantes</span></span></td>
                      <td className="p-3 text-right text-sm">{fmtL(it.monto)}</td>
                      <td className="p-3"><span className={`text-[10px] px-2 py-0.5 rounded ${est.bg} ${est.text}`}>{est.l}</span>{it.estado_reembolso === "reembolso_pendiente" && <div className="text-[9px] text-agro-yellow mt-0.5">Reembolso pendiente</div>}</td>
                      <td className="p-3 pr-5 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        {(it.estado === "borrador" || it.estado === "devuelto") && isOwner && <>
                          <button onClick={() => { setEditing(it); setForm({ ...it, fecha: (it.fecha || "").slice(0, 10) }); setOpen(true); }} className="p-1.5 text-text-muted hover:text-agro-green" title="Editar"><PencilSimpleIcon size={16} /></button>
                          <button onClick={() => submit(it.id)} className="p-1.5 text-text-muted hover:text-agro-green" title="Enviar a revisión" data-testid={`submit-${it.numero}`}><PaperPlaneTiltIcon size={16} weight="fill" /></button>
                          <button onClick={() => del(it.id)} className="p-1.5 text-text-muted hover:text-red-400" title="Eliminar" aria-label="Eliminar"><TrashIcon size={16} /></button>
                        </>}
                        {it.estado === "enviado" && canApprove && (it.solicitante_email !== user?.email || user?.role === "admin") && <>
                          <button onClick={() => approve(it.id)} className="p-1.5 text-text-muted hover:text-agro-green" title="Aprobar" data-testid={`approve-${it.numero}`}><CheckCircleIcon size={18} weight="fill" /></button>
                          <button onClick={() => ret(it.id)} className="p-1.5 text-text-muted hover:text-orange-400" title="Devolver" data-testid={`return-${it.numero}`}><ArrowUUpLeftIcon size={18} /></button>
                          <button onClick={() => reject(it.id)} className="p-1.5 text-text-muted hover:text-red-400" title="Denegar" data-testid={`reject-${it.numero}`}><XCircleIcon size={18} /></button>
                        </>}
                        {needsReimburse(it) && canReimburse && (
                          <button onClick={() => setReembModal(it)} className="p-1.5 text-text-muted hover:text-agro-green" title="Reembolsar" data-testid={`reimburse-${it.numero}`}><MoneyIcon size={18} weight="fill" /></button>
                        )}
                        <button onClick={() => setDetail(it)} className="p-1.5 text-text-muted hover:text-agro-yellow" title="Ver" aria-label="Ver"><EyeIcon size={16} /></button>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </>}

      {tab === "anticipos" && <AdvancesTab advances={advances} reps={reps} canManage={canReimburse} onChange={load} accounts={accounts} />}
      {tab === "reporte" && <ReportTab filtros={filtros} categorias={categorias} reps={reps} />}
      {tab === "categorias" && <>
        {(user?.role === "admin" || user?.role === "accountant") && <div className="mb-4"><ExpensesConfigPanel accounts={accounts} /></div>}
        <ExpenseCategories canManage={user?.role === "admin" || user?.role === "accountant"} onChange={load} />
      </>}

      {/* Modal nuevo/editar */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...em.overlayProps}>
          <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="expense-modal">
            <div className="p-5 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
              <h3 className="font-heading text-lg font-semibold">{editing ? `Editar ${editing.numero}` : "Nueva solicitud de gasto"}</h3>
              <button aria-label="Cerrar" onClick={em.cerrar} className="text-text-muted hover:text-text-primary"><XIcon size={20} /></button>
            </div>
            <form onSubmit={em.guardar} className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">Categoría *</label>
                  <select required value={form.categoria_id} onChange={(e) => setForm({...form, categoria_id: e.target.value})} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" data-testid="exp-categoria">
                    <option value="">— Seleccionar —</option>
                    {categorias.filter((c) => c.activa).map((c) => <option key={c.id} value={c.id}>{c.codigo} · {c.nombre}</option>)}
                  </select>
                </div>
                <F label="Monto (L.) *" type="number" v={form.monto} on={(v) => setForm({...form, monto: v})} required testid="exp-monto" />
                {fechaOp.fija ? (
                  <div><label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">Fecha del gasto</label>
                    <input type="date" value={fechaOp.hoy} disabled data-testid="exp-fecha"
                      className="w-full bg-sidebar border border-border rounded-md px-3 py-2 text-sm opacity-80" title="Tu rol registra solo con la fecha corriente de la empresa" /></div>
                ) : (
                  <F label="Fecha del gasto *" type="date" v={(form.fecha || "").slice(0, 10)} on={(v) => setForm({...form, fecha: v})} required testid="exp-fecha" />
                )}
                {catOtros(form.categoria_id) ? (
                  <F label="Especifique el gasto (Otros) *" v={form.categoria_otro} on={(v) => setForm({...form, categoria_otro: v})} required testid="exp-categoria-otro" />
                ) : <div />}
                <div className="md:col-span-2">
                  <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">Descripción *</label>
                  <input required value={form.descripcion} onChange={(e) => setForm({...form, descripcion: e.target.value})} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" data-testid="exp-descripcion" />
                </div>
                <F label="Región / ruta" v={form.region_ruta} on={(v) => setForm({...form, region_ruta: v})} testid="exp-region" />
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">Cliente / gestión</label>
                  <select value={form.cliente_id} onChange={(e) => setForm({...form, cliente_id: e.target.value})} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" data-testid="exp-cliente">
                    <option value="">— Opcional —</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.codigo} · {c.nombre}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">RTC asociado</label>
                  {isAdmin ? (
                    <select value={form.rtc_id} onChange={(e) => { const r = reps.find((x) => x.id === e.target.value); setForm({...form, rtc_id: e.target.value, region_ruta: form.region_ruta || r?.region || ""}); }} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" data-testid="exp-rtc">
                      <option value="">— Opcional —</option>
                      {reps.filter((r) => r.activo !== false).map((r) => <option key={r.id} value={r.id}>{r.codigo} · {r.nombre}</option>)}
                    </select>
                  ) : (
                    <div className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm text-text-secondary" data-testid="exp-rtc-fixed">{profile?.rtc ? `${profile.rtc.codigo} · ${profile.rtc.nombre}` : "Sin RTC vinculado a tu usuario"}</div>
                  )}
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">Método de pago *</label>
                  <select value={form.metodo_pago} onChange={(e) => setForm({...form, metodo_pago: e.target.value, adelanto_id: e.target.value === "contra_adelanto" ? form.adelanto_id : ""})} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" data-testid="exp-metodo">
                    {METODOS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
                  </select>
                </div>
                {form.metodo_pago === "contra_adelanto" && (
                  <div className="md:col-span-2">
                    <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">Anticipo a utilizar *</label>
                    <select required value={form.adelanto_id} onChange={(e) => setForm({...form, adelanto_id: e.target.value})} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" data-testid="exp-adelanto">
                      <option value="">— Seleccionar anticipo con saldo —</option>
                      {advances.filter((a) => a.estado !== "anulado" && a.estado !== "liquidado" && (!form.rtc_id || a.beneficiario_id === form.rtc_id)).map((a) => (
                        <option key={a.id} value={a.id}>{a.numero} · {a.beneficiario_nombre} · Saldo {fmtL(a.saldo_disponible)}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">Notas internas</label>
                <textarea rows={2} value={form.notas} onChange={(e) => setForm({...form, notas: e.target.value})} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm resize-none" />
              </div>
              <div className="bg-app rounded p-3 text-[11px] text-text-muted border border-border/40">
                <LightbulbIcon size={14} className="inline align-[-2px]" aria-hidden="true" /> Después de guardar, sube el respaldo (foto/PDF) desde el panel de detalle. Sin respaldo no se puede enviar a revisión.
              </div>
              <div className="flex justify-end gap-2 border-t border-border/60 pt-3">
                <button type="button" onClick={em.cerrar} className="px-4 py-2 text-sm border border-border rounded-md text-text-secondary">Cancelar</button>
                <button disabled={em.saving} type="submit" data-testid="exp-save" className="px-5 py-2 text-sm bg-agro-green text-black font-medium rounded-md">{editing ? "Guardar cambios" : "Crear borrador"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal detalle / respaldos */}
      {detail && (
        <ExpenseDetail e={detail} onClose={() => setDetail(null)} onUpload={uploadAttachment} onRemove={removeAttachment} onSubmit={submit} onApprove={approve} onReturn={ret} onReject={reject} canApprove={canApprove} isOwner={detail.solicitante_email === user?.email} onDistribute={() => setDistModal(detail)} />
      )}

      {/* Modal reembolso */}
      {/* Modal nuevo anticipo */}
      {advModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...am.overlayProps}>
          <div className="bg-card border border-border rounded-lg w-full max-w-md" data-testid="advance-modal">
            <div className="p-5 border-b border-border flex items-center justify-between">
              <h3 className="font-heading text-lg font-semibold flex items-center gap-2"><PiggyBankIcon size={20} className="text-agro-green" /> Nuevo anticipo</h3>
              <button aria-label="Cerrar" onClick={am.cerrar} className="text-text-muted hover:text-text-primary"><XIcon size={20} /></button>
            </div>
            <form onSubmit={am.guardar} className="p-5 space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">RTC beneficiario *</label>
                <select required value={advForm.beneficiario_id} onChange={(e) => setAdvForm({...advForm, beneficiario_id: e.target.value})} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" data-testid="adv-rtc">
                  <option value="">— Seleccionar —</option>
                  {reps.filter((r) => r.activo !== false).map((r) => <option key={r.id} value={r.id}>{r.codigo} · {r.nombre}</option>)}
                </select>
              </div>
              <F label="Monto (L.) *" type="number" v={advForm.monto} on={(v) => setAdvForm({...advForm, monto: v})} required testid="adv-monto" />
              <F label="Motivo / propósito" v={advForm.motivo} on={(v) => setAdvForm({...advForm, motivo: v})} testid="adv-motivo" />
              <div>
                <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">Cuenta egreso</label>
                <select value={advForm.cuenta_id} onChange={(e) => setAdvForm({...advForm, cuenta_id: e.target.value})} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" data-testid="adv-cuenta">
                  <option value="">— Caja por defecto —</option>
                  {accounts.map((a) => <option key={a.id} value={a.id}>{a.codigo} · {a.nombre}</option>)}
                </select>
              </div>
              <div className="bg-app rounded p-2 text-[11px] text-text-muted"><LightbulbIcon size={12} className="inline align-[-2px]" aria-hidden="true" /> Asiento automático: Debe 1107 Anticipos / Haber Caja. Se descuenta del saldo conforme se reembolsen gastos contra este anticipo.</div>
              <div className="flex justify-end gap-2 border-t border-border/60 pt-3">
                <button type="button" onClick={am.cerrar} className="px-4 py-2 text-sm border border-border rounded-md text-text-secondary">Cancelar</button>
                <button disabled={am.saving} type="submit" className="px-5 py-2 text-sm bg-agro-green text-black font-medium rounded-md" data-testid="adv-save">Entregar anticipo</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal asignar distribución */}
      {distModal && (
        <DistributionModal expense={distModal} onClose={() => setDistModal(null)} onSaved={(updated) => { setDistModal(null); setDetail(updated); load(); }} />
      )}
      {approveModal && (
        <ApproveModal expense={approveModal} onClose={() => setApproveModal(null)} onDone={() => { setApproveModal(null); setDetail(null); load(); }} />
      )}
      {motivoModal && (
        <MotivoModal title={motivoModal.kind === "reject" ? "Denegar gasto" : "Devolver para corrección"}
          label={motivoModal.kind === "reject" ? "Motivo de la denegación *" : "Motivo de la devolución (qué corregir) *"}
          confirmLabel={motivoModal.kind === "reject" ? "Denegar" : "Devolver"} danger={motivoModal.kind === "reject"}
          onClose={() => setMotivoModal(null)} onConfirm={confirmMotivo} />
      )}

      {reembModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...rm.overlayProps}>
          <div className="bg-card border border-border rounded-lg w-full max-w-md" data-testid="reimburse-modal">
            <div className="p-5 border-b border-border flex items-center justify-between">
              <h3 className="font-heading text-lg font-semibold">Reembolsar gasto</h3>
              <button aria-label="Cerrar" onClick={rm.cerrar} className="text-text-muted hover:text-text-primary"><XIcon size={20} /></button>
            </div>
            <form onSubmit={rm.guardar} className="p-5 space-y-3">
              <div className="bg-app p-3 rounded text-sm space-y-1">
                <div>N°: <span className="font-mono text-agro-green">{reembModal.numero}</span></div>
                <div>Solicitante: {reembModal.solicitante_nombre}</div>
                <div>Categoría: {reembModal.categoria_nombre}</div>
                <div>Monto: <span className="text-agro-green font-semibold">{fmtL(reembModal.monto)}</span></div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">Cuenta de egreso</label>
                <select value={reembCuenta} onChange={(e) => setReembCuenta(e.target.value)} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" data-testid="reimb-cuenta">
                  <option value="">— Caja por defecto —</option>
                  {accounts.map((a) => <option key={a.id} value={a.id}>{a.codigo} · {a.nombre}</option>)}
                </select>
              </div>
              <div className="flex justify-end gap-2 border-t border-border/60 pt-3">
                <button type="button" onClick={rm.cerrar} className="px-4 py-2 text-sm border border-border rounded-md text-text-secondary">Cancelar</button>
                <button disabled={rm.saving} type="submit" data-testid="reimb-confirm" className="px-5 py-2 text-sm bg-agro-green text-black font-medium rounded-md">Confirmar reembolso</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}

// ============== Componentes auxiliares ==============
function Kpi({ label, value, color, small }) {
  return (
    <div className="bg-card border border-border/60 rounded-lg p-3">
      <div className="text-text-muted text-[10px] uppercase tracking-wider">{label}</div>
      <div className={`${small ? "text-lg" : "text-2xl"} font-semibold ${color} mt-1`}>{value}</div>
    </div>
  );
}

function F({ label, v, on, type = "text", required, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">{label}</label>
      <input data-testid={testid} type={type} value={v ?? ""} required={required} onChange={(e) => on(e.target.value)} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" />
    </div>
  );
}

// ============== Modal detalle de gasto ==============
function ExpenseDetail({ e, onClose, onUpload, onRemove, onSubmit, onApprove, onReturn, onReject, canApprove, isOwner, onDistribute }) {
  const [file, setFile] = useState(null);
  const [desc, setDesc] = useState("");
  const editable = (e.estado === "borrador" || e.estado === "devuelto") && isOwner;
  const est = ESTADOS[e.estado];

  const dm = useModalForm({ onClose: onClose });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...dm.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="expense-detail">
        <div className="p-5 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
          <div>
            <h3 className="font-heading text-lg font-semibold">{e.numero}</h3>
            <div className="flex items-center gap-2 mt-1"><span className={`text-[10px] px-2 py-0.5 rounded ${est.bg} ${est.text}`}>{est.l}</span><span className="text-xs text-text-muted">{fmtDate(e.fecha)} · {e.solicitante_nombre}</span></div>
          </div>
          <button aria-label="Cerrar" onClick={dm.cerrar} className="text-text-muted hover:text-text-primary"><XIcon size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Info label="Categoría" value={`${e.categoria_codigo} · ${e.categoria_nombre}${e.categoria_otro ? ` (${e.categoria_otro})` : ""}`} />
            <Info label="Monto" value={fmtL(e.monto)} color="text-agro-green font-semibold" />
            <Info label="Región / ruta" value={e.region_ruta || "—"} />
            <Info label="Cliente / gestión" value={e.cliente_nombre || "—"} />
            <Info label="RTC" value={e.rtc_nombre || "—"} />
            <Info label="Método" value={(METODOS.find((m) => m.v === e.metodo_pago) || {}).l} />
            <div className="col-span-2"><Info label="Descripción" value={e.descripcion || "—"} /></div>
            {e.motivo_devolucion && <div className="col-span-2 bg-orange-500/10 border border-orange-500/40 rounded p-2"><div className="text-[10px] uppercase tracking-wider text-orange-400">Motivo de devolución</div><div className="text-sm">{e.motivo_devolucion}</div></div>}
            {(e.historial || []).length > 0 && (
              <div className="col-span-2" data-testid="expense-historial">
                <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1">Historial de cambios y revisión</div>
                <ul className="text-[11px] text-text-muted space-y-0.5">
                  {e.historial.map((h, i) => <li key={i}>{fmtDate(h.fecha)} · {h.accion} · {h.por}{h.observaciones ? ` — ${h.observaciones}` : ""}</li>)}
                </ul>
              </div>
            )}
            {e.motivo_rechazo && <div className="col-span-2 bg-red-500/10 border border-red-500/40 rounded p-2"><div className="text-[10px] uppercase tracking-wider text-red-400">Motivo del rechazo</div><div className="text-sm">{e.motivo_rechazo}</div></div>}
          </div>

          {/* Auditoría */}
          <div className="bg-app rounded p-3 text-[11px] text-text-muted space-y-0.5">
            {e.enviado_at && <div><PaperPlaneTiltIcon size={12} className="inline align-[-2px]" aria-hidden="true" /> Enviado: {fmtDate(e.enviado_at)}</div>}
            {e.devuelto_por && <div>↩️ Devuelto por {e.devuelto_por} el {fmtDate(e.devuelto_at)}</div>}
            {e.aprobado_por && <div><CheckCircleIcon size={12} className="inline align-[-2px]" aria-hidden="true" /> Aprobado por {e.aprobado_por} el {fmtDate(e.aprobado_at)}</div>}
            {e.denegado_por && <div><XCircleIcon size={12} className="inline align-[-2px]" aria-hidden="true" /> Denegado por {e.denegado_por} el {fmtDate(e.denegado_at)}</div>}
            {e.motivo_aprobacion && <div><ChatTextIcon size={12} className="inline align-[-2px]" aria-hidden="true" /> Comentario de aprobación: {e.motivo_aprobacion}</div>}
            {e.estado_reembolso === "reembolso_pendiente" && <div>⏳ Reembolso pendiente al empleado</div>}
            {e.reembolso_at && <div><MoneyIcon size={12} className="inline align-[-2px]" aria-hidden="true" /> Liquidado el {fmtDate(e.reembolso_at)}{e.reembolso_via ? ` (${e.reembolso_via})` : ""}</div>}
          </div>

          {/* Respaldos */}
          <div>
            <h4 className="font-heading text-sm uppercase tracking-wider text-agro-green mb-2 flex items-center gap-2"><CameraIcon size={16} /> Respaldos ({(e.comprobantes || []).length})</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-3">
              {(e.comprobantes || []).map((c, i) => (
                <div key={i} className="relative group bg-app border border-border/40 rounded p-2">
                  {c.url.startsWith("data:image/") ? <img src={c.url} alt={c.descripcion} className="w-full h-24 object-cover rounded" />
                    : <div className="w-full h-24 bg-card flex items-center justify-center rounded"><FileTextIcon size={28} className="text-text-muted" /></div>}
                  <div className="text-[10px] mt-1 truncate text-text-secondary">{c.descripcion}</div>
                  {editable && <button aria-label="Cerrar" onClick={() => onRemove(e.id, i)} className="absolute top-1 right-1 bg-red-500/80 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100"><XIcon size={12} /></button>}
                </div>
              ))}
            </div>
            {editable && (
              <div className="bg-app border border-dashed border-border rounded p-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
                  <div className="md:col-span-2">
                    <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">Foto/PDF de respaldo *</label>
                    <input type="file" accept="image/*,.pdf" onChange={(ev) => setFile(ev.target.files?.[0])} className="w-full text-xs" data-testid="att-file" />
                    <input placeholder="Descripción (opcional)" value={desc} onChange={(ev) => setDesc(ev.target.value)} className="w-full bg-card border border-border rounded px-2 py-1.5 text-xs mt-2" />
                  </div>
                  <button onClick={async () => { if (file) { await onUpload(e.id, file, desc); setFile(null); setDesc(""); } }} disabled={!file} className="bg-agro-green text-black px-4 py-2 text-sm rounded-md font-medium disabled:opacity-40 flex items-center justify-center gap-1" data-testid="att-upload"><CameraIcon size={14} weight="bold" /> Subir</button>
                </div>
              </div>
            )}
          </div>

          {/* Distribución por proveedor */}
          {(canApprove || (e.distribucion || e.distribucion_proveedores || []).length > 0) && (
            <div>
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-heading text-sm uppercase tracking-wider text-agro-yellow flex items-center gap-2"><PercentIcon size={14} /> Distribución %</h4>
                {canApprove && (e.estado === "enviado" || e.estado === "aprobado") && (
                  <button onClick={onDistribute} data-testid="detail-distribute" className="text-xs px-3 py-1 border border-agro-yellow/40 text-agro-yellow rounded-md hover:bg-agro-yellow/10">{(e.distribucion || e.distribucion_proveedores || []).length > 0 ? "Editar %" : "+ Asignar %"}</button>
                )}
              </div>
              {(e.distribucion || []).length === 0 && (e.distribucion_proveedores || []).length === 0 ? (
                <div className="text-[11px] text-text-muted bg-app rounded p-2">Sin distribución asignada (opcional para el reporte de inversión).</div>
              ) : (
                <div className="bg-app rounded p-2 space-y-1">
                  {((e.distribucion || []).length ? e.distribucion : e.distribucion_proveedores.map((d) => ({ tipo: "proveedor", nombre: d.proveedor_nombre, porcentaje: d.porcentaje, monto: e.monto * d.porcentaje / 100 }))).map((d, i) => (
                    <div key={i} className="flex justify-between text-xs"><span><span className="text-[9px] uppercase text-text-muted mr-1">{d.tipo}</span>{d.nombre}</span><span className="text-agro-yellow">{d.porcentaje}% · {fmtL(d.monto)}</span></div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Acciones */}
          <div className="flex justify-end gap-2 border-t border-border/60 pt-3">
            {editable && (e.comprobantes || []).length > 0 && (
              <button onClick={() => onSubmit(e.id)} data-testid="detail-submit" className="px-4 py-2 text-sm bg-agro-green text-black font-medium rounded-md flex items-center gap-1"><PaperPlaneTiltIcon size={14} weight="bold" /> Enviar a revisión</button>
            )}
            {e.estado === "enviado" && canApprove && !isOwner && <>
              <button onClick={() => onReject(e.id)} data-testid="detail-reject" className="px-4 py-2 text-sm border border-red-500/40 text-red-400 rounded-md hover:bg-red-500/10">Denegar</button>
              <button onClick={() => onReturn(e.id)} data-testid="detail-return" className="px-4 py-2 text-sm border border-orange-500/40 text-orange-400 rounded-md hover:bg-orange-500/10">Devolver</button>
              <button onClick={() => onApprove(e.id)} data-testid="detail-approve" className="px-4 py-2 text-sm bg-agro-green text-black font-medium rounded-md">Aprobar</button>
            </>}
          </div>
        </div>
      </div>
    </div>
  );
}

function Info({ label, value, color }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-text-secondary mb-0.5">{label}</div>
      <div className={`text-sm ${color || "text-text-primary"}`}>{value}</div>
    </div>
  );
}

// ============== Categorías configurables ==============
function ExpenseCategories({ canManage, onChange }) {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState({});
  const [open, setOpen] = useState(false);
  const [nuevo, setNuevo] = useState({ codigo: "", nombre: "", ejemplo: "", cuenta_contable: "6106", orden: 99, activa: true });

  const load = async () => { const { data } = await api.get("/expenses/categories"); setRows(data); };
  useEffect(() => { load(); }, []);

  const getVal = (r, k) => (edit[r.id]?.[k] ?? r[k]);
  const save = async (r) => {
    const patch = edit[r.id] || {};
    if (!Object.keys(patch).length) return;
    try { await api.put(`/expenses/categories/${r.id}`, { ...r, ...patch, orden: Number(patch.orden ?? r.orden) }); toast.success("Categoría actualizada"); setEdit({ ...edit, [r.id]: undefined }); load(); onChange?.(); }
    catch (er) { toast.error(apiErr(er)); }
  };
  const del = async (r) => { if (!window.confirm(`¿Eliminar/desactivar "${r.nombre}"?`)) return; try { const { data } = await api.delete(`/expenses/categories/${r.id}`); toast.success(data.disabled ? "Desactivada (tiene gastos asociados)" : "Eliminada"); load(); onChange?.(); } catch (er) { toast.error(apiErr(er)); } };
  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/expenses/categories", { ...nuevo, orden: Number(nuevo.orden) }); toast.success("Categoría agregada"); setOpen(false); setNuevo({ codigo: "", nombre: "", ejemplo: "", cuenta_contable: "6106", orden: 99, activa: true }); load(); onChange?.(); }
    catch (er) { toast.error(apiErr(er)); }
  };

  const cm = useModalForm({ value: nuevo, abierto: open, onClose: () => setOpen(false), onSave: create });
  return (
    <div className="space-y-4">
      <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
        <div className="p-3 border-b border-border/60 flex items-center justify-between">
          <div>
            <h4 className="font-heading text-sm uppercase tracking-wider text-agro-green">Categorías de gasto (configurables)</h4>
            <p className="text-[11px] text-text-muted mt-0.5">Cada categoría tiene su propia cuenta contable para el asiento del reembolso.</p>
          </div>
          {canManage && <button onClick={() => setOpen(true)} className="bg-agro-green text-black px-3 py-1.5 text-xs rounded-md font-medium flex items-center gap-1" data-testid="add-cat-btn"><PlusIcon size={14} weight="bold" /> Nueva categoría</button>}
        </div>
        <table className="w-full">
          <thead className="bg-sidebar">
            <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
              <th className="p-3 pl-5">Código</th><th className="p-3">Nombre</th>
              <th className="p-3">Ejemplo</th><th className="p-3">Cuenta contable</th>
              <th className="p-3 text-right">Orden</th><th className="p-3 text-center">Activa</th>
              <th className="p-3 text-right pr-5">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan="7" className="p-8 text-center text-text-muted">Sin categorías</td></tr>
              : rows.map((r) => {
                const dirty = !!edit[r.id] && Object.keys(edit[r.id]).length > 0;
                return (
                  <tr key={r.id} className="border-t border-border/40 hover:bg-white/[0.02]" data-testid={`cat-row-${r.codigo}`}>
                    <td className="p-3 pl-5 font-mono text-xs text-agro-green">{r.codigo}</td>
                    <td className="p-3"><input value={getVal(r, "nombre")} disabled={!canManage} onChange={(e) => setEdit({...edit, [r.id]: {...(edit[r.id]||{}), nombre: e.target.value}})} className="bg-app border border-border rounded px-2 py-1 text-sm w-full" /></td>
                    <td className="p-3"><input value={getVal(r, "ejemplo") ?? ""} disabled={!canManage} onChange={(e) => setEdit({...edit, [r.id]: {...(edit[r.id]||{}), ejemplo: e.target.value}})} className="bg-app border border-border rounded px-2 py-1 text-xs w-full" /></td>
                    <td className="p-3"><input value={getVal(r, "cuenta_contable")} disabled={!canManage} onChange={(e) => setEdit({...edit, [r.id]: {...(edit[r.id]||{}), cuenta_contable: e.target.value}})} className="bg-app border border-border rounded px-2 py-1 text-xs w-24 font-mono" /></td>
                    <td className="p-3 text-right"><input type="number" value={getVal(r, "orden")} disabled={!canManage} onChange={(e) => setEdit({...edit, [r.id]: {...(edit[r.id]||{}), orden: e.target.value}})} className="bg-app border border-border rounded px-2 py-1 text-xs w-16 text-right" /></td>
                    <td className="p-3 text-center"><input type="checkbox" checked={getVal(r, "activa")} disabled={!canManage} onChange={(e) => setEdit({...edit, [r.id]: {...(edit[r.id]||{}), activa: e.target.checked}})} className="accent-agro-green" /></td>
                    <td className="p-3 pr-5 text-right flex justify-end gap-1">
                      {canManage && dirty && <button onClick={() => save(r)} title="Guardar" className="p-1.5 text-agro-green hover:bg-agro-green/10 rounded" data-testid={`save-cat-${r.codigo}`}><FloppyDiskIcon size={16} weight="bold" /></button>}
                      {canManage && <button aria-label="Eliminar" onClick={() => del(r)} className="p-1.5 text-text-muted hover:text-red-400"><TrashIcon size={16} /></button>}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...cm.overlayProps}>
          <div className="bg-card border border-border rounded-lg w-full max-w-md" data-testid="cat-new-modal">
            <div className="p-5 border-b border-border flex items-center justify-between">
              <h3 className="font-heading text-lg font-semibold">Nueva categoría de gasto</h3>
              <button aria-label="Cerrar" onClick={cm.cerrar} className="text-text-muted hover:text-text-primary"><XIcon size={20} /></button>
            </div>
            <form onSubmit={cm.guardar} className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <F label="Código (CORTO) *" v={nuevo.codigo} on={(v) => setNuevo({...nuevo, codigo: v.toUpperCase()})} required testid="cat-new-codigo" />
                <F label="Orden" type="number" v={nuevo.orden} on={(v) => setNuevo({...nuevo, orden: v})} />
              </div>
              <F label="Nombre *" v={nuevo.nombre} on={(v) => setNuevo({...nuevo, nombre: v})} required testid="cat-new-nombre" />
              <F label="Ejemplo / descripción" v={nuevo.ejemplo} on={(v) => setNuevo({...nuevo, ejemplo: v})} />
              <F label="Cuenta contable" v={nuevo.cuenta_contable} on={(v) => setNuevo({...nuevo, cuenta_contable: v})} />
              <div className="flex justify-end gap-2 border-t border-border/60 pt-3">
                <button type="button" onClick={cm.cerrar} className="px-4 py-2 text-sm border border-border rounded-md text-text-secondary">Cancelar</button>
                <button disabled={cm.saving} type="submit" className="px-5 py-2 text-sm bg-agro-green text-black font-medium rounded-md" data-testid="cat-new-save">Agregar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ============== TAB: ANTICIPOS ==============
function AdvancesTab({ advances, reps, canManage, onChange, accounts }) {
  const [detail, setDetail] = useState(null);
  const [details, setDetails] = useState(null);
  const open = async (a) => {
    try { const { data } = await api.get(`/advances/${a.id}`); setDetail(a); setDetails(data); } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (a) => {
    if (!window.confirm(`¿Anular anticipo ${a.numero}? Se revertirá la salida de tesorería.`)) return;
    try { await api.delete(`/advances/${a.id}`); toast.success("Anticipo anulado"); onChange?.(); } catch (e) { toast.error(apiErr(e)); }
  };

  const totales = {
    entregado: advances.filter((a) => a.estado !== "anulado").reduce((s, a) => s + a.monto, 0),
    pendiente: advances.filter((a) => a.estado !== "anulado").reduce((s, a) => s + a.saldo_disponible, 0),
    activos: advances.filter((a) => a.estado === "entregado" || a.estado === "liquidado_parcial").length,
  };

  const EST = {
    entregado: { l: "Entregado", bg: "bg-agro-yellow/20", t: "text-agro-yellow" },
    liquidado_parcial: { l: "Liquidado parcial", bg: "bg-blue-500/20", t: "text-blue-400" },
    liquidado: { l: "Liquidado", bg: "bg-agro-green/20", t: "text-agro-green" },
    anulado: { l: "Anulado", bg: "bg-text-muted/20", t: "text-text-muted" },
  };

  const vm = useModalForm({ abierto: Boolean(detail && details), onClose: () => { setDetail(null); setDetails(null); } });
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-card border border-border/60 rounded-lg p-4"><div className="text-text-muted text-[10px] uppercase tracking-wider">Total entregado</div><div className="text-xl font-semibold text-text-primary mt-1">{fmtL(totales.entregado)}</div></div>
        <div className="bg-card border border-border/60 rounded-lg p-4"><div className="text-text-muted text-[10px] uppercase tracking-wider">Saldo pendiente liquidar</div><div className="text-xl font-semibold text-agro-yellow mt-1">{fmtL(totales.pendiente)}</div></div>
        <div className="bg-card border border-border/60 rounded-lg p-4"><div className="text-text-muted text-[10px] uppercase tracking-wider">Anticipos activos</div><div className="text-2xl font-semibold text-agro-green mt-1">{totales.activos}</div></div>
      </div>
      <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-sidebar">
            <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
              <th className="p-3 pl-5">N° / Fecha</th><th className="p-3">Beneficiario</th>
              <th className="p-3">Motivo</th><th className="p-3 text-right">Monto entregado</th>
              <th className="p-3 text-right">Saldo</th><th className="p-3">Estado</th>
              <th className="p-3 text-right pr-5">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {advances.length === 0 ? <tr><td colSpan="7" className="p-8 text-center text-text-muted">Sin anticipos.</td></tr>
              : advances.map((a) => {
                const e = EST[a.estado] || EST.entregado;
                return (
                  <tr key={a.id} className="border-t border-border/40 hover:bg-white/[0.02]" data-testid={`adv-row-${a.numero}`}>
                    <td className="p-3 pl-5"><div className="font-mono text-xs text-agro-green">{a.numero}</div><div className="text-[10px] text-text-muted">{fmtDate(a.fecha)}</div></td>
                    <td className="p-3 text-sm">{a.beneficiario_nombre}</td>
                    <td className="p-3 text-xs text-text-secondary">{a.motivo || "—"}</td>
                    <td className="p-3 text-right text-sm">{fmtL(a.monto)}</td>
                    <td className="p-3 text-right text-sm text-agro-yellow">{fmtL(a.saldo_disponible)}</td>
                    <td className="p-3"><span className={`text-[10px] px-2 py-0.5 rounded ${e.bg} ${e.t}`}>{e.l}</span></td>
                    <td className="p-3 pr-5 text-right">
                      <button onClick={() => open(a)} className="p-1.5 text-text-muted hover:text-agro-yellow" title="Ver movimientos" data-testid={`view-adv-${a.numero}`}><EyeIcon size={16} /></button>
                      {canManage && a.estado === "entregado" && <button onClick={() => del(a)} className="p-1.5 text-text-muted hover:text-red-400" title="Anular" aria-label="Anular"><TrashIcon size={16} /></button>}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {detail && details && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...vm.overlayProps}>
          <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[92vh] overflow-y-auto">
            <div className="p-5 border-b border-border flex items-center justify-between">
              <h3 className="font-heading text-lg font-semibold">Anticipo {detail.numero}</h3>
              <button aria-label="Cerrar" onClick={vm.cerrar} className="text-text-muted hover:text-text-primary"><XIcon size={20} /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Info label="Beneficiario" value={detail.beneficiario_nombre} />
                <Info label="Estado" value={(EST[detail.estado] || {}).l} />
                <Info label="Entregado" value={fmtL(detail.monto)} color="text-text-primary font-semibold" />
                <Info label="Saldo disponible" value={fmtL(detail.saldo_disponible)} color="text-agro-yellow font-semibold" />
                <div className="col-span-2"><Info label="Motivo" value={detail.motivo || "—"} /></div>
                <Info label="Cuenta egreso" value={detail.cuenta_nombre || "—"} />
                <Info label="Fecha" value={fmtDate(detail.fecha)} />
              </div>
              <div>
                <h4 className="text-xs uppercase tracking-wider text-agro-green mb-2 mt-3">Movimientos ({details.movements.length})</h4>
                <table className="w-full text-xs">
                  <thead className="bg-sidebar"><tr><th className="p-2 text-left">Tipo</th><th className="p-2 text-left">Descripción</th><th className="p-2 text-right">Monto</th><th className="p-2 text-left">Fecha</th></tr></thead>
                  <tbody>
                    {details.movements.length === 0 ? <tr><td colSpan="4" className="p-4 text-center text-text-muted">Sin movimientos aún</td></tr>
                      : details.movements.map((m) => (
                        <tr key={m.id} className="border-t border-border/40">
                          <td className="p-2 uppercase text-[10px]">{m.tipo}</td>
                          <td className="p-2">{m.descripcion}</td>
                          <td className={`p-2 text-right ${m.monto < 0 ? "text-red-400" : "text-agro-green"}`}>{fmtL(m.monto)}</td>
                          <td className="p-2 text-[10px] text-text-muted">{fmtDate(m.fecha)}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============== TAB: REPORTE ==============
function ReportTab({ filtros, categorias, reps }) {
  const [data, setData] = useState(null);
  const [rfiltros, setRfiltros] = useState({ desde: "", hasta: "", rtc_id: "", categoria_id: "", region: "" });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(rfiltros).filter(([_, v]) => v));
      const { data } = await api.get("/expenses/report", { params });
      setData(data);
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [rfiltros]);

  if (loading || !data) return <div className="text-center text-text-muted py-12">Generando reporte…</div>;

  return (
    <div className="space-y-4">
      {/* Filtros del reporte */}
      <div className="bg-card border border-border/60 rounded-lg p-3">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
          <input type="date" value={rfiltros.desde} onChange={(e) => setRfiltros({...rfiltros, desde: e.target.value})} className="bg-app border border-border rounded px-2 py-1.5" data-testid="rep-desde" />
          <input type="date" value={rfiltros.hasta} onChange={(e) => setRfiltros({...rfiltros, hasta: e.target.value})} className="bg-app border border-border rounded px-2 py-1.5" data-testid="rep-hasta" />
          <select value={rfiltros.rtc_id} onChange={(e) => setRfiltros({...rfiltros, rtc_id: e.target.value})} className="bg-app border border-border rounded px-2 py-1.5">
            <option value="">Todos los RTC</option>
            {reps.map((r) => <option key={r.id} value={r.id}>{r.codigo} · {r.nombre}</option>)}
          </select>
          <select value={rfiltros.categoria_id} onChange={(e) => setRfiltros({...rfiltros, categoria_id: e.target.value})} className="bg-app border border-border rounded px-2 py-1.5">
            <option value="">Todas las categorías</option>
            {categorias.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </select>
          <input placeholder="Región…" value={rfiltros.region} onChange={(e) => setRfiltros({...rfiltros, region: e.target.value})} className="bg-app border border-border rounded px-2 py-1.5" />
        </div>
      </div>

      {/* KPIs globales */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="Solicitudes" value={data.total_count} color="text-text-primary" />
        <Kpi label="Monto total" value={fmtL(data.total_monto)} color="text-agro-green" small />
        <Kpi label="Anticipos entregados" value={fmtL(data.anticipos.total_entregado)} color="text-text-primary" small />
        <Kpi label="Saldo anticipos" value={fmtL(data.anticipos.saldo_pendiente)} color="text-agro-yellow" small />
        <Kpi label="# Anticipos" value={data.anticipos.count} color="text-blue-400" />
      </div>

      {/* Por estado + Por RTC */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ReportBlock title="Por estado" rows={data.por_estado} keyL="estado" />
        <ReportBlock title="Por RTC" rows={data.por_rtc} keyL="rtc_nombre" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ReportBlock title="Por categoría" rows={data.por_categoria} keyL="categoria_nombre" />
        <ReportBlock title="Por región / ruta" rows={data.por_region} keyL="region" />
      </div>

      <ExpenseReportExtras data={data} params={rfiltros} />

      {/* Por proveedor (distribución %) */}
      <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
        <div className="p-3 border-b border-border/60"><h4 className="font-heading text-sm uppercase tracking-wider text-agro-green flex items-center gap-2"><PercentIcon size={14} /> Inversión por proveedor (vía % asignado)</h4></div>
        <table className="w-full">
          <thead className="bg-sidebar"><tr className="text-left text-[10px] uppercase tracking-wider text-text-muted"><th className="p-3 pl-5">Proveedor</th><th className="p-3 text-right">Solicitudes</th><th className="p-3 text-right pr-5">Monto asignado</th></tr></thead>
          <tbody>
            {data.por_proveedor.length === 0 ? <tr><td colSpan="3" className="p-8 text-center text-text-muted">Sin distribuciones asignadas. Asignar % al aprobar un gasto en la bandeja.</td></tr>
              : data.por_proveedor.sort((a, b) => b.monto - a.monto).map((p) => (
                <tr key={p.proveedor_id} className="border-t border-border/40"><td className="p-3 pl-5 text-sm">{p.proveedor_nombre}</td><td className="p-3 text-right text-xs">{p.count}</td><td className="p-3 pr-5 text-right text-sm text-agro-green">{fmtL(p.monto)}</td></tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ReportBlock({ title, rows, keyL }) {
  const sum = rows.reduce((s, r) => s + r.total, 0);
  return (
    <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
      <div className="p-3 border-b border-border/60"><h4 className="font-heading text-sm uppercase tracking-wider text-agro-green">{title}</h4></div>
      <table className="w-full">
        <thead className="bg-sidebar"><tr className="text-left text-[10px] uppercase tracking-wider text-text-muted"><th className="p-2 pl-4">Etiqueta</th><th className="p-2 text-right">#</th><th className="p-2 text-right pr-4">Monto</th></tr></thead>
        <tbody>
          {rows.length === 0 ? <tr><td colSpan="3" className="p-4 text-center text-text-muted text-xs">Sin datos</td></tr>
            : rows.sort((a, b) => b.total - a.total).map((r, i) => (
              <tr key={i} className="border-t border-border/40 hover:bg-white/[0.02]">
                <td className="p-2 pl-4 text-xs">{r[keyL] || "—"}</td>
                <td className="p-2 text-right text-xs">{r.count}</td>
                <td className="p-2 pr-4 text-right text-xs">{fmtL(r.total)}</td>
              </tr>
            ))}
          {sum > 0 && <tr className="border-t border-border/60 bg-app"><td className="p-2 pl-4 text-xs uppercase tracking-wider text-text-muted">Total</td><td className="p-2 text-right text-xs">{rows.reduce((s, r) => s + r.count, 0)}</td><td className="p-2 pr-4 text-right text-xs text-agro-green font-semibold">{fmtL(sum)}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
